/**
 * useWaitingQueue — the pinned pane's queue: a SERVER read, invalidated by
 * user-scoped push, with a poll as the correctness floor.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`).
 * Modelled on `useInstancePush` (adaptive cadence, `lastSuccessAt`, `isStale`,
 * visibility handling), NOT on `apps/outlook-addin/src/hooks/useActiveInstances.ts`
 * — that is the add-in's active-tab list.
 *
 * THE RULE THIS HOOK EXISTS TO ENFORCE (design Constraint 19): the rows and
 * the counts are ALWAYS the server's answer. A push event triggers a refetch
 * and nothing else — no fold, no increment, no optimistic removal. The push
 * channel is an INVALIDATION signal; treating its frames as data would build a
 * second, drifting copy of a number the server already computes.
 *
 * Cadence (visible tab unless stated):
 *   push connected      → 30s   heartbeat; push drives the interesting updates
 *   push unavailable    →  5s   polling is the only feed, so run it fast
 *   connecting / other  → 60s   idle; hammering the endpoint does not help
 *   tab hidden          → 30s
 *
 * With push unavailable the queue is still CORRECT, only slower.
 *
 * STALENESS is measured against the ACTIVE cadence, never the wall clock. A
 * fixed window cannot be right for a poll that runs at 5s, 30s or 60s: the old
 * fixed 30s, set against the 60s idle cadence, marked the queue stale for half
 * of every cycle while everything was healthy — which teaches people to ignore
 * the one notice that is supposed to mean something. `lastError` still flips
 * staleness IMMEDIATELY, so a failing API is caught on its first bad response;
 * the elapsed-time path is only a backstop for a poll loop that has stopped
 * delivering at all, and a backstop can afford to be generous.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserPush } from "../signalr/useUserPush";
import { WORKFLOW_EVENT_METHOD } from "../signalr/useSignalR";
import type { GetUserPushChannelFn } from "../signalr/signalrClient";
import type { WaitingForMeResponse, WaitingQueueItem } from "../types";

/** How many whole cadences may elapse before a missed refresh counts. One
 *  missed tick is indistinguishable from jitter or a slow request; two mean
 *  the loop is genuinely not delivering. */
const STALE_CADENCE_MULTIPLE = 2;

/** Headroom for request latency: `schedule()` starts the next timer AFTER the
 *  previous fetch resolves, so two successes are always at least
 *  `cadence + request duration` apart, never exactly one cadence. */
const STALE_LATENCY_GRACE_MS = 5_000;

/** Why the queue is stale. `"error"` is a fetch that FAILED; `"age"` is a fetch
 *  that has simply not happened recently enough. Different facts, and the pane
 *  is required to say different things about them. */
export type WaitingQueueStaleReason = "error" | "age";

/**
 * THE cadence rule — one definition, read by both the poll timer and the
 * staleness window. Two copies of this branch would be two rules that can
 * disagree, and their disagreement presents as a phantom "couldn't refresh".
 */
export function cadenceFor(args: {
  hidden: boolean;
  isConnected: boolean;
  isSignalRUnavailable: boolean;
  pushedIntervalMs: number;
  unavailableIntervalMs: number;
  idleIntervalMs: number;
  hiddenIntervalMs: number;
}): number {
  if (args.hidden) return args.hiddenIntervalMs;
  if (args.isConnected && !args.isSignalRUnavailable) {
    return args.pushedIntervalMs;
  }
  if (args.isSignalRUnavailable) return args.unavailableIntervalMs;
  return args.idleIntervalMs;
}

export interface UseWaitingQueueOptions {
  /** Fetch the queue. Wraps the Remix fetcher in ora and
   *  `orchestratorApi.listWaiting` in the add-in. */
  listWaiting: (limit?: number) => Promise<WaitingForMeResponse>;
  /** Resolve the user-scoped negotiate descriptor. */
  getUserPushChannel: GetUserPushChannelFn;
  /** Page size forwarded to the server. */
  limit?: number;
  enabled?: boolean;
  /** Disable side effects in tests. */
  disabled?: boolean;
  /** Cadence when push is connected. Default 30000ms. */
  pushedIntervalMs?: number;
  /** Cadence when push is unavailable. Default 5000ms. */
  unavailableIntervalMs?: number;
  /** Cadence when push is neither connected nor unavailable. Default 60000ms. */
  idleIntervalMs?: number;
  /** Cadence on a hidden tab. Default 30000ms. */
  hiddenIntervalMs?: number;
  /**
   * Override the staleness window. Defaults to a window DERIVED from the
   * active cadence (`cadence * 2 + 5s`), so it tracks the poll rather than
   * contradicting it. Pass a number only to pin the window in a test.
   */
  staleAfterMs?: number;
}

export interface UseWaitingQueueResult {
  items: WaitingQueueItem[];
  /** The SERVER's count, never items.length. */
  count: number;
  /** The SERVER's notifiable count — the badge's number. */
  needsYouCount: number;
  runningCount: number | null;
  triagedToday: number | null;
  asOf: string | null;
  lastError: Error | null;
  lastSuccessAt: number | null;
  /** True when the last fetch failed, or nothing has succeeded within the
   *  staleness window. Either way the rows must not be presented as current. */
  isStale: boolean;
  /** WHY `isStale` is true, so the surface can distinguish "we could not read
   *  the queue" from "we have not read it lately". `null` when not stale. */
  staleReason: WaitingQueueStaleReason | null;
  loading: boolean;
  refetch: () => Promise<boolean>;
  isPushConnected: boolean;
  isSignalRUnavailable: boolean;
}

export function useWaitingQueue(
  options: UseWaitingQueueOptions,
): UseWaitingQueueResult {
  const {
    listWaiting,
    getUserPushChannel,
    limit = 50,
    enabled = true,
    disabled = false,
    pushedIntervalMs = 30_000,
    unavailableIntervalMs = 5_000,
    idleIntervalMs = 60_000,
    hiddenIntervalMs = 30_000,
    staleAfterMs,
  } = options;

  // Held by ref so an unmemoized caller arrow doesn't rebuild the poll timer
  // on every render (same reasoning as useInstancePush).
  const listWaitingRef = useRef(listWaiting);
  listWaitingRef.current = listWaiting;

  const [items, setItems] = useState<WaitingQueueItem[]>([]);
  const [count, setCount] = useState(0);
  const [needsYouCount, setNeedsYouCount] = useState(0);
  const [runningCount, setRunningCount] = useState<number | null>(null);
  const [triagedToday, setTriagedToday] = useState<number | null>(null);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState<number>(Date.now());
  // Mirrors document.visibilityState so the staleness window resolves the same
  // cadence the timer is actually running. Kept in sync by the same
  // visibilitychange listener that reschedules the poll.
  const [hidden, setHidden] = useState<boolean>(
    () =>
      typeof document !== "undefined" && document.visibilityState === "hidden",
  );

  const mountedRef = useRef(true);

  // Request generation. TWO feeds call `fetchOnce` — the push subscription and
  // the cadence timer — so two reads are routinely in flight at once, and HTTP
  // makes no promise about which resolves first. Without this, the LAST
  // RESPONSE won unconditionally: a delayed poll that started before a
  // push-triggered refetch could land after it and overwrite fresher rows with
  // its own, and the queue would sit a full cadence behind with nothing on
  // screen saying so. The counter makes the LAST REQUEST STARTED the only one
  // allowed to write, which is the ordering the reader actually cares about.
  const genRef = useRef(0);

  const fetchOnce = useCallback(async (): Promise<boolean> => {
    const myGen = (genRef.current += 1);
    setLoading(true);
    try {
      const next = await listWaitingRef.current(limit);
      if (!mountedRef.current) return false;
      // Superseded while in flight — discard. Deliberately silent: a newer
      // read is already on its way, and reporting this as an error would flip
      // the queue to stale over a race that cost nothing.
      if (myGen !== genRef.current) return false;
      const rows = Array.isArray(next?.items) ? next.items : [];
      setItems(rows);
      // The server's own numbers. `count` may legitimately exceed
      // `items.length` when the page is truncated, so it is never recomputed
      // from the rows.
      setCount(typeof next?.count === "number" ? next.count : rows.length);
      setNeedsYouCount(
        typeof next?.needs_you_count === "number" ? next.needs_you_count : 0,
      );
      setRunningCount(
        typeof next?.running_count === "number" ? next.running_count : null,
      );
      setTriagedToday(
        typeof next?.triaged_today === "number" ? next.triaged_today : 0,
      );
      setAsOf(next?.as_of ?? null);
      setLastSuccessAt(Date.now());
      setLastError(null);
      return true;
    } catch (err) {
      if (!mountedRef.current) return false;
      // A SUPERSEDED read's failure is not this queue's failure either — the
      // newer request is the one whose outcome the pane is showing.
      if (myGen !== genRef.current) return false;
      const e = err instanceof Error ? err : new Error(String(err));
      // Deliberately does NOT clear the rows. A failed fetch leaves the
      // previous rows on screen labelled stale; replacing them with an empty
      // list would render "Nothing waiting on you" over a queue we simply
      // could not read — the exact untruth AC-3 exists to prevent.
      setLastError(e);
      console.warn("[useWaitingQueue] fetch failed", { error: e.message });
      return false;
    } finally {
      // Only the live generation may clear `loading`; a superseded read
      // resolving first would otherwise report "done" while the read that
      // actually owns the next state is still out.
      if (mountedRef.current && myGen === genRef.current) setLoading(false);
    }
  }, [limit]);

  const refetch = useCallback(async (): Promise<boolean> => {
    return await fetchOnce();
  }, [fetchOnce]);

  const push = useUserPush({
    getUserPushChannel,
    enabled: enabled && !disabled,
    disabled,
  });

  // Push is an INVALIDATION signal. The handler reads nothing off the frame.
  useEffect(() => {
    if (!enabled || disabled) return;
    const off = push.subscribe(WORKFLOW_EVENT_METHOD, () => {
      void fetchOnce();
    });
    return off;
    // `push.subscribe` is the only member read here and it is a stable
    // useCallback; depending on `push` itself would re-subscribe on every
    // isConnected flip for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, disabled, push.subscribe, fetchOnce]);

  // ── Polling floor ───────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || disabled) {
      return () => {
        mountedRef.current = false;
      };
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const isHidden = (): boolean =>
      typeof document !== "undefined" && document.visibilityState === "hidden";

    const cadence = (): number =>
      cadenceFor({
        hidden: isHidden(),
        isConnected: push.isConnected,
        isSignalRUnavailable: push.isSignalRUnavailable,
        pushedIntervalMs,
        unavailableIntervalMs,
        idleIntervalMs,
        hiddenIntervalMs,
      });

    const schedule = () => {
      if (cancelled) return;
      timer = setTimeout(async () => {
        await fetchOnce();
        schedule();
      }, cadence());
    };

    void fetchOnce().then(schedule);

    const onVisibility = () => {
      if (timer) clearTimeout(timer);
      setHidden(isHidden());
      if (!isHidden()) void fetchOnce();
      schedule();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [
    enabled,
    disabled,
    fetchOnce,
    pushedIntervalMs,
    unavailableIntervalMs,
    idleIntervalMs,
    hiddenIntervalMs,
    push.isConnected,
    push.isSignalRUnavailable,
  ]);

  // Tick `now` so `isStale` flips without a fetch having to happen.
  useEffect(() => {
    if (disabled) return;
    const id = setInterval(() => {
      if (mountedRef.current) setNow(Date.now());
    }, 5_000);
    return () => clearInterval(id);
  }, [disabled]);

  // Resolved from the same inputs the poll timer uses, through the same
  // `cadenceFor`, so the window can never contradict the poll it is judging.
  const activeCadenceMs = useMemo(
    () =>
      cadenceFor({
        hidden,
        isConnected: push.isConnected,
        isSignalRUnavailable: push.isSignalRUnavailable,
        pushedIntervalMs,
        unavailableIntervalMs,
        idleIntervalMs,
        hiddenIntervalMs,
      }),
    [
      hidden,
      push.isConnected,
      push.isSignalRUnavailable,
      pushedIntervalMs,
      unavailableIntervalMs,
      idleIntervalMs,
      hiddenIntervalMs,
    ],
  );

  const effectiveStaleAfterMs =
    staleAfterMs ??
    activeCadenceMs * STALE_CADENCE_MULTIPLE + STALE_LATENCY_GRACE_MS;

  // Independent of whether push is connected: a live socket in front of a dead
  // API is precisely the case that would otherwise present a stale list as
  // current. A FAILED fetch is stale IMMEDIATELY — the elapsed window exists
  // only for a poll that has gone quiet without erroring, which is why it is
  // counted in cadences rather than in fixed milliseconds.
  const staleReason = useMemo<WaitingQueueStaleReason | null>(() => {
    if (lastError) return "error";
    if (lastSuccessAt === null) return null;
    return now - lastSuccessAt > effectiveStaleAfterMs ? "age" : null;
  }, [lastError, now, lastSuccessAt, effectiveStaleAfterMs]);

  const isStale = staleReason !== null;

  return {
    items,
    count,
    needsYouCount,
    runningCount,
    triagedToday,
    asOf,
    lastError,
    lastSuccessAt,
    isStale,
    staleReason,
    loading,
    refetch,
    isPushConnected: push.isConnected,
    isSignalRUnavailable: push.isSignalRUnavailable,
  };
}

export default useWaitingQueue;
